// Diesel tanks and machine costs. The formulas are the ones the two pages have always used.

export const PORTABLE_NAME = 'Rezervor Portabil (1T)';
export const EQUIPMENT_TYPES = ['Tractor', 'Combină', 'Plug', 'Semănătoare', 'Grapă', 'Cultivator', 'Sprayer', 'Remorcă', 'Încărcător', 'Altele'];

const today = () => new Date().toISOString().split('T')[0];
const positive = (text) => parseFloat(String(text).replace(',', '.')) > 0;

export const emptyFuel = () => ({ tankCapacity: 15000, portableTankCapacity: 1000, purchases: [], dispensing: [] });
export const normalizeFuel = (saved) => (saved?.tankCapacity ? saved : { ...emptyFuel(), ...(saved || {}) });

export const computeLevels = (data) => {
  const mainIn = (data.purchases || []).reduce((s, p) => s + (p.liters || 0), 0);
  const mainOut = (data.dispensing || []).filter((d) => d.fromTank === 'main').reduce((s, d) => s + (d.liters || 0), 0);
  const portIn = (data.dispensing || []).filter((d) => d.toType === 'portable').reduce((s, d) => s + (d.liters || 0), 0);
  const portOut = (data.dispensing || []).filter((d) => d.fromTank === 'portable').reduce((s, d) => s + (d.liters || 0), 0);
  return { mainLevel: Math.max(0, mainIn - mainOut), portableLevel: Math.max(0, portIn - portOut) };
};

export const computeAvgPrice = (purchases) => {
  const liters = (purchases || []).reduce((s, p) => s + (p.liters || 0), 0);
  const cost = (purchases || []).reduce((s, p) => s + (p.liters || 0) * (p.pricePerLiter || 0), 0);
  return liters > 0 ? cost / liters : 0;
};

export const fuelTotals = (data) => ({
  litersIn: (data.purchases || []).reduce((s, p) => s + (p.liters || 0), 0),
  litersOut: (data.dispensing || []).reduce((s, d) => s + (d.liters || 0), 0),
  cost: (data.purchases || []).reduce((s, p) => s + (p.liters || 0) * (p.pricePerLiter || 0), 0),
});

// How much can leave a tank. A line being edited gives its own litres back first.
export const availableIn = (data, tank, editing) => {
  const { mainLevel, portableLevel } = computeLevels(data);
  const level = tank === 'main' ? mainLevel : portableLevel;
  return editing && editing.fromTank === tank ? level + (editing.liters || 0) : level;
};

export const fuelEvents = (data) => [
  ...(data.purchases || []).map((p) => ({ ...p, _type: 'purchase' })),
  ...(data.dispensing || []).map((d) => ({ ...d, _type: 'dispensing' })),
].sort((a, b) => new Date(b.date) - new Date(a.date));

// Litres per machine, under the name the machine has today.
export const utilajReport = (data, utilaje = []) => {
  const avgPrice = computeAvgPrice(data.purchases);
  const map = {};
  (data.dispensing || []).filter((d) => d.toType === 'utilaj').forEach((d) => {
    const key = d.toId || d.toName;
    if (!map[key]) map[key] = { key, name: utilaje.find((u) => String(u.id) === String(d.toId))?.name || d.toName, liters: 0 };
    map[key].liters += d.liters || 0;
  });
  return Object.values(map).sort((a, b) => b.liters - a.liters).map((row) => ({ ...row, cost: row.liters * avgPrice }));
};

export const emptyPurchase = () => ({ date: today(), liters: '', pricePerLiter: '', supplier: '', note: '' });
export const emptyDispensing = () => ({ date: today(), fromTank: 'main', toType: 'utilaj', toId: '', toName: '', liters: '', driver: '', note: '' });

const litersOf = (text) => +String(text).replace(',', '.');

export const purchaseError = (form) => {
  if (!positive(form.liters) || !positive(form.pricePerLiter)) return 'Completează litrii și prețul pe litru.';
  return form.date ? '' : 'Alege data.';
};

export const buildPurchase = (form, id = Date.now()) => ({
  ...form, id, liters: litersOf(form.liters), pricePerLiter: litersOf(form.pricePerLiter),
});

export const dispensingError = (form, data, editing) => {
  if (!form.toId) return 'Alege unde merge motorina.';
  if (!positive(form.liters)) return 'Completează litrii.';
  if (!form.date) return 'Alege data.';
  const available = availableIn(data, form.fromTank, editing);
  if (litersOf(form.liters) > available + 0.01) {
    return `Stoc insuficient în rezervorul ${form.fromTank === 'main' ? 'principal' : 'portabil'}: ${available.toFixed(0)} L disponibili.`;
  }
  return '';
};

export const buildDispensing = (form, id = Date.now()) => ({ ...form, id, liters: litersOf(form.liters) });

export const replaceOrAdd = (list, record, isEdit) => (isEdit ? (list || []).map((item) => (item.id === record.id ? record : item)) : [...(list || []), record]);

// ---------- Machines ----------

export const getEqCostBreakdown = (eq) => {
  let totalAchizitie = 0;
  if (eq.acquisitionType === 'cash') {
    totalAchizitie = eq.purchasePrice || 0;
  } else if (eq.acquisitionType === 'leasing') {
    totalAchizitie = (eq.avans || 0) + (eq.annualInstallment || 0) * (eq.leasingYears || 1);
  }
  const valRevanzare = eq.valoareRevanzare || 0;
  const aniPastrare = eq.aniPastrare || eq.amortizationYears || (eq.leasingYears || 1);
  const netCost = Math.max(0, totalAchizitie - valRevanzare);
  const costAnual = aniPastrare > 0 ? netCost / aniPastrare : 0;
  // Value "locked" in the asset per year (recoverable at resale)
  const valBlocataAnual = aniPastrare > 0 ? valRevanzare / aniPastrare : 0;
  return { totalAchizitie, valRevanzare, netCost, aniPastrare, costAnual, valBlocataAnual };
};

export const calcAnnualCost = (eq, year) => {
  let acquisition = 0;
  const { costAnual, aniPastrare } = getEqCostBreakdown(eq);
  const start = eq.acquisitionType === 'cash' ? (eq.purchaseYear || year) : (eq.leasingStartYear || year);
  const end = start + aniPastrare - 1;
  if (year >= start && year <= end) acquisition = costAnual;

  let repairs = 0;
  (eq.repairs || []).forEach((r) => {
    const repYear = parseInt(r.date?.slice(0, 4) || 0, 10);
    if (r.amortizationType === 'multi') {
      const years = r.amortizationYears || 1;
      if (year >= repYear && year <= repYear + years - 1) repairs += (r.cost || 0) / years;
    } else if (repYear === year) {
      repairs += (r.cost || 0);
    }
  });
  return { acquisition, repairs, total: acquisition + repairs };
};

export const repairPerYear = (repair) => (repair.cost || 0) / (repair.amortizationType === 'multi' ? (repair.amortizationYears || 1) : 1);

// The fuel a machine received in a year, and what it cost at the average price.
export const fuelForEquipment = (data, eq, year) => {
  const fills = (data.dispensing || [])
    .filter((d) => d.toType === 'utilaj' && String(d.toId) === String(eq.id) && String(d.date || '').startsWith(String(year)))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const liters = fills.reduce((s, d) => s + (d.liters || 0), 0);
  return { fills, liters, cost: liters * computeAvgPrice(data.purchases) };
};

export const emptyEquipment = (year = new Date().getFullYear()) => ({
  name: '', type: 'Tractor', brand: '', model: '', year: String(year), acquisitionType: 'cash',
  purchasePrice: '', purchaseYear: String(year), amortizationYears: '10',
  avans: '', annualInstallment: '', leasingYears: '5', leasingStartYear: String(year),
  valoareRevanzare: '', aniPastrare: '', notes: '',
});

export const formFromEquipment = (eq) => ({
  ...emptyEquipment(), ...eq,
  year: String(eq.year ?? ''),
  purchasePrice: String(eq.purchasePrice || ''), purchaseYear: String(eq.purchaseYear || new Date().getFullYear()),
  amortizationYears: String(eq.amortizationYears || ''), avans: String(eq.avans || ''),
  annualInstallment: String(eq.annualInstallment || ''), leasingYears: String(eq.leasingYears || ''),
  leasingStartYear: String(eq.leasingStartYear || new Date().getFullYear()),
  valoareRevanzare: String(eq.valoareRevanzare || ''), aniPastrare: String(eq.aniPastrare || ''),
});

const amount = (text) => Number(String(text || 0).replace(',', '.')) || 0;

export const buildEquipment = (form, existing, id = Date.now()) => {
  const year = new Date().getFullYear();
  return {
    ...form,
    id: existing?.id || id,
    purchasePrice: amount(form.purchasePrice),
    purchaseYear: parseInt(form.purchaseYear || year, 10),
    amortizationYears: parseInt(form.amortizationYears || 1, 10),
    avans: amount(form.avans),
    annualInstallment: amount(form.annualInstallment),
    leasingYears: parseInt(form.leasingYears || 1, 10),
    leasingStartYear: parseInt(form.leasingStartYear || year, 10),
    valoareRevanzare: amount(form.valoareRevanzare),
    aniPastrare: form.aniPastrare ? parseInt(form.aniPastrare, 10) : 0,
    repairs: existing?.repairs || [],
  };
};

export const emptyRepair = () => ({ date: today(), description: '', cost: '', amortizationType: 'current', amortizationYears: '3' });
export const formFromRepair = (repair) => ({ ...repair, cost: String(repair.cost), amortizationYears: String(repair.amortizationYears || 3) });
export const buildRepair = (form, id = Date.now()) => ({
  ...form, id, cost: amount(form.cost), amortizationYears: parseInt(form.amortizationYears || 1, 10),
});
export const repairYearsText = (form) => {
  const start = parseInt(form.date?.slice(0, 4), 10);
  const years = parseInt(form.amortizationYears, 10) || 1;
  if (!start) return '';
  const list = Array.from({ length: Math.min(years, 12) }, (_, index) => start + index);
  return list.length > 1 ? `${list.slice(0, -1).join(', ')} și ${list[list.length - 1]}` : String(list[0]);
};
